import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { getIndividualsByUserIds, getIndividualsPendingByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useSelector } from "react-redux";
import TeamTemplate from "work/team-templates/team-template";
import ActiveTeamDisplay from "_shared/teams/active-team-display";
import SearchMatch from "_shared/_views/tables/search-match";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";


const TeamColumnLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  display: "inline-block",
  textDecorationColor: theme.palette.common.black,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
}));


type LeaderColumnCellProps<TData extends MRT_RowData> = {
  teamTemplate?: TeamTemplate;
  teamUserIds?: Guid[];
  activeTeam?: ActiveTeamDisplay;
  /** The table the cell sits in, whose search the name marks. Only where that search covers the leader (#6590). */
  table?: MRT_TableInstance<TData>;
};


export default function LeaderColumnCell<TData extends MRT_RowData>(props: Readonly<LeaderColumnCellProps<TData>>) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  const { teamTemplate, teamUserIds, activeTeam, table } = props;
  const { openDialog, closeDialog } = useDialog();

  let leaderUserId: Guid | undefined;
  let individualsUserIds: Guid[] = [];

  if (activeTeam) {
    leaderUserId = activeTeam.leader?.userId;
    individualsUserIds = [activeTeam.leader?.userId].filter(Boolean) as Guid[];
  } else if (teamTemplate) {
    leaderUserId = teamTemplate.leader?.userId;
    individualsUserIds = [teamTemplate.leader?.userId].filter(Boolean) as Guid[];
  } else if (teamUserIds) {
    individualsUserIds = teamUserIds;
    leaderUserId = teamUserIds[0]; // fallback, not ideal
  }

  const teamIndividualsResponse = useSelector(getIndividualsByUserIds(individualsUserIds));
  const isLookingUpLeader = useSelector(getIndividualsPendingByUserIds(leaderUserId ? [leaderUserId] : []));
  const leaderIndividual = leaderUserId
    ? teamIndividualsResponse.find((ind) => ind.userId.isEqualTo(leaderUserId!))
    : undefined;
  if (!leaderIndividual && isLookingUpLeader) return <Skeleton animation="wave" width={120} />;
  if (!leaderIndividual) return null;

  return (
    <TeamColumnLink
      onClick={(event) => {
        event.stopPropagation();
        openDialog({
          component: <ViewIndividualProfile individualId={leaderIndividual.id} />,
          contentSxProps: {
            display: "flex",
          },
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          titleStyle: {
            paddingBottom: 0
          },
          onClose: closeDialog,
        });
      }}
    >
      {table ? <SearchMatch table={table} text={leaderIndividual.fullName} /> : leaderIndividual.fullName}
    </TeamColumnLink>
  );
}
